import { CheckBox, Clear, FlashOffRounded } from "@mui/icons-material";
import {
  CircularProgress,
  Divider,
  Fab,
  FilledInput,
  FormControl,
  FormControlLabel,
  Grid,
  Input,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import axios from "axios";
import cx from "classnames";
import { differenceInYears, format, parseISO as parseDate } from "date-fns";
import React, { useEffect, useState } from "react";
import { DiscountTypes, UserTypes } from "../../../common/Constants";
import NoteConfirmation from "../../../common/NoteConfirmation";
import { endpoints, useApi } from "../../../hooks/use-api";
import { useNotification } from "../../../hooks/use-notification";
import VoucherActionList from "./VoucherActionList";
import VoucherDetailSection from "./VoucherDetailSection";
import ButtonOption from "./ButtonOption";
import { Link } from "react-router-dom";

function age(dateOfBirth) {
  try {
    return (
      differenceInYears(
        new Date(),
        parseDate(dateOfBirth.substring(0, 10), "yyyy-MM-dd", null)
      ) + " years"
    );
  } catch (e) {
    return "";
  }
}

const parseFloatOrDefault = (value) => {
  let floatValue = parseFloat(value);
  return isNaN(floatValue) ? 0.0 : floatValue;
};

const DispenserSection = ({ voucher, status, redeemClicked, paidClicked }) => {
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Divider style={{ marginTop: "12px", marginBottom: "12px" }} />
        <Typography variant="button" color="textSecondary">
          Dispenser
        </Typography>
        <Typography variant="h6">FEA Industries Inc.</Typography>
        {status === "approved" && (
          <NoteConfirmation
            buttonLabel="Mark as Redeemed"
            onConfirm={(note) => redeemClicked(note)}
          />
        )}
        {status === "redeemed" && (
          <>
            {/* Shipping info:<br />
                        <UspsTrack /> */}
            <NoteConfirmation
              buttonLabel="Mark as Paid"
              onConfirm={(note) => paidClicked(note)}
            />
          </>
        )}
        {status === "paid" && <Typography variant="button">Paid</Typography>}
      </Grid>
    </Grid>
  );
};

const FinancialSection = ({ voucher, allowChange, reloadfn }) => {
  const [financial, setFinancial] = useState({
    base_amount: 0.0,
    upgrade_amount: 0.0,
    program_fee: 0.0,
    patient_paid: 0.0,
    discount_pct: 0.0,
    total_patient_cost: 0.0,
  });
  const [amountDisable, setAmountDisable] = useState(true);
  const [upgradeAmountDisable, setUpgradeAmountDisable] = useState(true);
  const [refundClicked, setRefundClicked] = useState(false);
  //?? const {base_amount, upgrade_amount, patient_paid, dispenser_cost} = financial

  const [computedFinancial, setComputedFinancial] = useState({
    discount_amount: 0.0,
    dispenser_cost: 0.0,
    patient_cost: 0.0,
    total_patient_cost: 0.0,
  });

  const { set: setNotification } = useNotification();
  const [financialForReset, setFinancialForReset] = useState({});

  useEffect(() => {
    setFinancial(voucher.voucher_financial);
  }, [voucher]);

  useEffect(() => {
    var discount = parseFloatOrDefault(financial.program_fee) * financial.discount_pct;
    setComputedFinancial({
      ...computedFinancial,
      discount_amount: discount,
      dispenser_cost:
        parseFloatOrDefault(financial.base_amount) +
        parseFloatOrDefault(financial.upgrade_amount),
      patient_cost:
        parseFloatOrDefault(financial.program_fee) +
        parseFloatOrDefault(financial.reissue_fee) -
        parseFloatOrDefault(discount),
      total_patient_cost:
        parseFloatOrDefault(financial.program_fee) +
        parseFloatOrDefault(financial.reissue_fee) +
        parseFloatOrDefault(financial.upgrade_amount) -
        parseFloatOrDefault(discount),
    });
  }, [financial]);

  const updateFinancial = (note) => {
    axios
      .put(endpoints.entity.voucher.updateFinancial(voucher.id), {
        base_amount: parseFloatOrDefault(financial.base_amount),
        upgrade_amount: parseFloatOrDefault(financial.upgrade_amount),
        program_fee: parseFloatOrDefault(financial.program_fee),
        patient_paid: parseFloatOrDefault(financial.patient_paid),
        dispenser_cost: parseFloatOrDefault(computedFinancial.dispenser_cost),
        program_fee: parseFloatOrDefault(financial.program_fee),
        reissue_fee: parseFloatOrDefault(financial.reissue_fee),
        discount_pct: parseFloatOrDefault(financial.discount_pct),
        note: note,
      })
      .then(function (response) {
        setNotification({
          message: `Updated voucher status`,
          type: "success",
        });
        reloadfn(voucher.id);
      })
      .catch(function (error) {
        setNotification({
          message: `Error ${error.message}`,
          type: "error",
        });
      });
  };

  const onChangeAmount = () => {
    setFinancialForReset({ ...financial });
    setAmountDisable(false);
  }

  const onChangeUpgradeAmountCancel = () => {
    setFinancial({
      ...financial,
      upgrade_amount: financialForReset.upgrade_amount,
    });
    setUpgradeAmountDisable(true);
  }

  const onClickUpgradeAmount = (note) => {
    setFinancialForReset({ ...financial });
    setUpgradeAmountDisable(false);
  }  

  const onChangeUpgradeAmount = (note) => {
    axios
      .put(endpoints.entity.voucher.upgradeAmount(voucher.id), {
        upgradeAmount: financial.upgrade_amount,
        note: note,
      })
      .then(function (response) {
        setNotification({
          message: `Upgrade amount successful`,
          type: "success",
        });
        reloadfn(voucher.id);
      })
      .catch(function (error) {
        setNotification({
          message: `Error ${error.message}`,
          type: "error",
        });
      });
  }

  const onRefundAmount = (note) => {
    axios
      .put(endpoints.entity.voucher.refund(voucher.id), {
        note: note,
      })
      .then(function (response) {
        setNotification({
          message: `Refund successful`,
          type: "success",
        });
        reloadfn(voucher.id);
      })
      .catch(function (error) {
        setNotification({
          message: `Error ${error.message}`,
          type: "error",
        });
      });
  }

  const onChangeAmountCancel = () => {
    setFinancial({
      ...financial,
      reissue_fee: financialForReset.reissue_fee,
      patient_paid: financialForReset.patient_paid,
      discount_pct: financialForReset.discount_pct,
    });
    setAmountDisable(true);
  }

  // const handleDiscountChange = (e) => {
  //   var discount = parseFloatOrDefault(financial.program_fee) * e.target.value;
  //   setFinancial({
  //     ...financial,
  //     discount_pct: e.target.value,
  //     discount_amount: discount,
  //     patient_cost:
  //       parseFloatOrDefault(financial.program_fee) +
  //       parseFloatOrDefault(financial.reissue_fee) +
  //       parseFloatOrDefault(financial.upgrade_amount) -
  //       parseFloatOrDefault(discount),
  //   });
  // }

  if (!financial) {
    return <div></div>;
  }

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Divider style={{ marginTop: "12px", marginBottom: "12px" }} />
        <Typography variant="button" color="textSecondary">
          Financial
        </Typography>
      </Grid>

      <Grid container item xs={6} style={{ borderRight: '2px solid gray', paddingRight: '10px' }}>        
        <Grid item xs={12}>
          <Grid item xs={12}>
            <Typography variant="caption" color="textPrimary" fontWeight="bold">
              Cost of Eyeglass($):
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ marginTop: "12px", marginBottom: "12px", bgcolor: "ActiveBorder", borderBottomWidth: 2 }} />
          </Grid>
        </Grid>  

        <Grid item xs={8}>
          <Typography variant="caption" color="textPrimary">
            Base Price:
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Input
            size="small"
            fullWidth
            value={financial.base_amount}
            inputProps={{ style: { textAlign: 'right', marginRight: '10px' } }} 
            startAdornment={<InputAdornment position="start">$</InputAdornment>}
            disabled={true}
          />
        </Grid>
        <Grid item xs={8}>
          <Typography variant="caption" color="textPrimary">
            Upgrade Price:
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <TextField
            hiddenLabel
            fullWidth
            size="small"
            value={financial.upgrade_amount}
            disabled={upgradeAmountDisable}
            inputProps={{ style: { textAlign: 'right' } }}
            startAdornment={<InputAdornment position="start">$</InputAdornment>}
            variant="filled"
            onChange={(e) => setFinancial({ ...financial, upgrade_amount: e.target.value })}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="caption" size="small" color="GrayText">
            * Upgrade price is collected outside of this system
          </Typography>
        </Grid>
      </Grid>

      <Grid container item xs={6} spacing={1}>
        <Grid item xs={12}>
          <Grid item xs={12}>
            <Typography variant="caption" color="textPrimary" fontWeight="bold">
              Applicant Dues collected by New Eyes($):
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ marginTop: "12px", marginBottom: "12px", bgcolor: "ActiveBorder", borderBottomWidth: 2 }} />
          </Grid>
        </Grid>  

        <Grid item xs={8}>
          <Typography variant="caption" color="textPrimary">
            Program Fee:
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <TextField
            hiddenLabel
            fullWidth
            size="small"
            value={financial.program_fee}
            disabled={amountDisable}
            inputProps={{ style: { textAlign: 'right' } }}
            startAdornment={<InputAdornment position="start">$</InputAdornment>}
            variant="filled"
            onChange={(e) => setFinancial({ ...financial, program_fee: e.target.value })}
          />          
        </Grid>

        <Grid item xs={8}>
          <Typography variant="caption" color="textPrimary">
            Program Fee Discount:
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Select
            hiddenLabel
            fullWidth
            label="Fee Discount (%)"
            value={financial.discount_pct}
            size="small"
            onChange={(e) => setFinancial({ ...financial, discount_pct: e.target.value })}
            disabled={amountDisable}
            variant="filled"
          >
            {DiscountTypes.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.name}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item xs={8}>
          <Typography variant="caption" color="textPrimary">
            Reissue Price:
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <TextField
            hiddenLabel
            fullWidth
            size="small"
              value={financial.reissue_fee}
              disabled={amountDisable}
              inputProps={{ style: { textAlign: 'right' } }}
              startAdornment={<InputAdornment position="start">$</InputAdornment>}
              variant="filled"
              onChange={(e) => setFinancial({ ...financial, reissue_fee: e.target.value })}
            />
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Divider sx={{ marginTop: "12px", marginBottom: "12px", bgcolor: "ActiveBorder", borderBottomWidth: 2 }} />
      </Grid>

      <Grid container item xs={6} style={{ borderRight: '2px solid gray', paddingRight: '10px' }}>
          <Grid item xs={8}>
            <Typography variant="caption" color="textPrimary">
              Cost of Eyeglass($):
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Input
              fullWidth
              value={computedFinancial.dispenser_cost}
              inputProps={{ style: { textAlign: 'right', marginRight: '10px' } }}
              startAdornment={<InputAdornment position="start">$</InputAdornment>}
              disabled={true}
            />
        </Grid>
        <Grid item xs={8}>
          <Typography variant="caption" color="textPrimary">
            Total Cost to Applicant($):
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Input
            fullWidth
            value={computedFinancial.total_patient_cost}
            inputProps={{ style: { textAlign: 'right', marginRight: '10px' } }}
            startAdornment={<InputAdornment position="start">$</InputAdornment>}
            disabled={true}
          />
        </Grid>
        
      </Grid>
      <Grid container item xs={6} spacing={1}>
        <Grid item xs={8}>
          <Typography variant="caption" color="textPrimary">
            Discount($):
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Input
            fullWidth
            value={-computedFinancial.discount_amount}
            inputProps={{ style: { textAlign: 'right', marginRight: '10px' } }}
            startAdornment={<InputAdornment position="start">$</InputAdornment>}
            disabled={true}
          />
        </Grid>
        <Grid item xs={8}>
          <Typography variant="caption" color="textPrimary">
            Applicant dues ($):
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Input
            fullWidth
            value={computedFinancial.patient_cost}
            inputProps={{ style: { textAlign: 'right', marginRight: '10px' } }}
            startAdornment={<InputAdornment position="start">$</InputAdornment>}
            disabled={true}
          />
        </Grid>
        <Grid item xs={8}>
          <Typography variant="caption" color="textPrimary">
            Received From Applicant($):
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <TextField
            hiddenLabel
            fullWidth
            value={financial.patient_paid}
            inputProps={{ style: { textAlign: 'right' } }}
            startAdornment={<InputAdornment position="start">$</InputAdornment>}
            disabled={amountDisable}
            size="small"
            variant="filled"
            error={parseFloatOrDefault(financial.patient_paid) < parseFloatOrDefault(computedFinancial.patient_cost)}
            onChange={(e) =>
              setFinancial({
                ...financial,
                patient_paid: e.target.value
              })
            }
          />
        </Grid>      
      </Grid>
      {allowChange && (
        <Grid item xs={12} sx={{ paddingTop: '10px' }}>
          {financial.patient_paid > 0 && upgradeAmountDisable && (
            <NoteConfirmation
              buttonLabel="Refund to applicant"
              confirmButtonLabel="Refund to original payment method"
              noteRequired={true}
              onClick={() => setRefundClicked(true)}
              onCancel={() => setRefundClicked(false)}
              onConfirm={(note) => onRefundAmount(note)}
            />
          )}
          {financial.patient_paid == 0 && upgradeAmountDisable && (
            <NoteConfirmation
              buttonLabel="Update Amount"
              confirmButtonLabel="Update"
              label="Why changed the amount?"
              noteRequired={true}
              onClick={onChangeAmount}
              onCancel={onChangeAmountCancel}
              onConfirm={(note) => updateFinancial(note)}
            />
          )}
          {!refundClicked && amountDisable && (
            <NoteConfirmation
              buttonLabel="Update Upgrade Price Only"
              confirmButtonLabel="Update Upgrade Price"
              label="What are the upgrades offered to the applicant?"
              noteRequired={true} 
              onClick={onClickUpgradeAmount}
              onCancel={onChangeUpgradeAmountCancel}
              onConfirm={(note) => onChangeUpgradeAmount(note)}
            />
          )}
        </Grid>
      )}
    </Grid>
  );
};

const ApprovedOrderPanel = ({
  individual_application,
  require_vision_test,
  prescription_renewed,
  order_uuid,
}) => {
  return (
    <div
      style={{
        backgroundColor: "#82E0AA",
        textAlign: "center",
        verticalAlign: "middle",
      }}
    >
      <Divider style={{ marginTop: "12px", marginBottom: "12px" }} />
      <Typography variant="h6" color="textPrimary">
        Your voucher has been approved!
      </Typography>

      {require_vision_test && !prescription_renewed && (
        <>
          {/* <Typography variant="subtitle2" color="textPrimary">
            Applicant must complete the online vision test to renew the
            prescription first.
          </Typography>
          <Typography variant="subtitle1" color="textPrimary">
            <b>Option 1</b>: Send applicant email link for a vision test
          </Typography>
          <Button
            variant="contained"
            color="primary"
            sx={{ my: 1, mx: 2, width: "90%" }}
            onClick={(e) => e}
          >
            Send applicant email for online vision test
          </Button>
          <Typography variant="subtitle1" color="textPrimary">
            <b>Option 2</b>: If you are assising applicant to take vision test
            on this computer and ready to do the vision test right here
          </Typography>
          <Button
            variant="contained"
            color="primary"
            sx={{ my: 1, mx: 2, width: "90%" }}
            onClick={(e) => e}
          >
            Go to vision test now
          </Button> */}
          <Typography variant="subtitle2" color="textPrimary">
            The link to online vision test is emailed to you and the applicant. Please have the applicant take the online vision
            test. Once they receive the new prescription. Please order your glasses by clicking the following link:     
          </Typography>
          <Link to={`/order/${order_uuid}`}>
            <button>Order here</button>
          </Link>
        </>
      )}
      {(!require_vision_test || prescription_renewed) && (
        <>
          <Typography variant="subtitle2" color="textPrimary">
            Please order your glasses by clicking the following link:
          </Typography>
          <Link to={`/order/${order_uuid}`} target="_blank">
            <button>Order here</button>
          </Link>
        </>
      )}          
      <br />
      <br />
    </div>
  );
};

const VoucherPanel = ({
  voucher: voucher2,
  userType,
  onEdit,
  onClose,
  onAction,
}) => {
  // Load voucher detail page
  const [{ data: voucher, loading: voucherLoading, error: voucherError }] =
    useApi(endpoints.entity.voucher.loadOne(voucher2.id), { useCache: false });

  const [addNoteMode, setAddNoteMode] = useState({
    showNote: false,
    required: true,
    noteLabel: "Type the note",
    action: "addNoteOnly",
  });
  const [acionNote, setActionNote] = useState("");

  const { set: setNotification } = useNotification();

  const setVoucherStatus = (id, action) => {
    axios
      .put(endpoints.entity.voucher.updateOne(id), action)
      .then(function (response) {
        setNotification({
          message: `Updated voucher status`,
          type: "success",
        });
        onAction(id, action);
      })
      .catch(function (error) {
        setNotification({
          message: `Error ${error.message}`,
          type: "error",
        });
      });
  };

  //??  useEffect(() => console.log(voucher), [voucher]);

  if (voucher2.id === 0) {
    return <div>Select a voucher</div>;
  }

  if (voucherLoading) return <CircularProgress />;
  if (voucherError)
    return (
      <Typography variant="caption" color="error" component="div">
        Failed to load voucher
      </Typography>
    );
  
  var current = voucher.actions[0];

  return (
    <React.Fragment>
      {voucherLoading && <CircularProgress />}
      {voucherError && (
        <Typography variant="caption" color="error" component="div">
          Failed to load voucher
        </Typography>
      )}
      {voucher && (
        <div style={{ padding: "24px" }}>
          <Grid container>
            <Grid item xs={10}>
              <Grid container>
                <Grid item xs={12}>
                  <Typography variant="h6" color="textSecondary">
                    VOUCHER # {voucher.id}
                    {voucher.voucher_detail.is_reissued &&
                      <>&nbsp;&nbsp;
                      <Typography
                        variant="button"
                        style={{
                          marginRight: "2px",
                          padding: "6px",
                          border: "1px solid lightgray",
                          borderRadius: "4px",
                          textTransform: "uppercase",
                          backgroundColor: "#dde8ed",
                        }}
                      >
                        reissued
                      </Typography>
                      </>
                    }
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="button"
                    className={cx("voucher-status", current.status)}
                  >
                    {current.status}
                  </Typography>
                  {current.datetime && (
                    <Typography
                      variant="body2"
                      component="span"
                      style={{ marginLeft: "24px" }}
                    >
                      {format(new Date(current.datetime), "MMM dd, yyyy")}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={2}>
              <Fab aria-label="cancel" onClick={onClose}>
                <Clear />
              </Fab>
              {/* { allowEdit && <Button variant='button' color='primary' onClick={onEdit} >EDIT</Button> } */}
            </Grid>
            {current.status === "rejected" && !!current.note && (
              <Grid item xs={12} style={{ marginTop: "8px" }}>
                <Typography variant="caption" color="error" component="div">
                  {current.note}
                </Typography>
              </Grid>
            )}
          </Grid>
          {userType === UserTypes.neweyes &&
            (current.status === "approved" ||
              current.status === "redeemed" ||
              current.status === "paid") && (
              <>
                <FinancialSection
                  voucher={voucher}
                  allowChange={current.status !== "paid" && current.status !== "redeemed"}
                  reloadfn={(id) => onAction(id, null)}
                />
                <DispenserSection
                  voucher={voucher}
                  status={current.status}
                  redeemClicked={(note) =>
                    setVoucherStatus(voucher.id, {
                      status: "redeemed",
                      note: note,
                      sendEmail: false,
                    })
                  }
                  paidClicked={(note) =>
                    setVoucherStatus(voucher.id, {
                      status: "paid",
                      note: note,
                      sendEmail: false,
                    })
                  }
                />
              </>
            )}
          {/* Show glass ordering page if the voucher is approved and online vision test is done (if required) */}
          {userType !== UserTypes.neweyes && current.status === "approved" && (
            <ApprovedOrderPanel
              individual_application={
                voucher.voucher_detail.individual_application
              }
              require_vision_test={voucher.voucher_detail.require_vision_test}
              prescription_renewed={voucher.voucher_detail.prescription_renewed}
              order_uuid={voucher.voucher_financial.order_uuid}
            />
          )}
          <VoucherDetailSection
            voucher={voucher}
            agencySummary={voucher.agencySummary}
            onClose={onClose}
          />
          {userType === UserTypes.neweyes && (
            // current.status === "pending" &&
            <React.Fragment>
              {addNoteMode.showNote ? (
                <React.Fragment>
                  {addNoteMode.confirmNote && (
                    <Typography fullWidth variant="h5" color="primary">
                      {addNoteMode.confirmNote}
                    </Typography>
                  )}
                  {addNoteMode.noteLabel && (
                    <TextField
                      label={addNoteMode.noteLabel}
                      required={addNoteMode.required}
                      fullWidth
                      multiline
                      rows="4"
                      defaultValue={acionNote}
                      onChange={(e) => setActionNote(e.target.value)}
                      margin="dense"
                      variant="outlined"
                    />
                  )}
                  {addNoteMode.action === "reissue" && (
                    <Button
                      variant="contained"
                      fullWidth
                      color="primary"
                      style={{ marginTop: "24px" }}
                      disabled={addNoteMode.required && acionNote.length === 0}
                      onClick={(e) =>
                        setVoucherStatus(voucher.id, {
                          status: "reissue",
                          note: acionNote,
                          sendEmail: true,
                        })
                      }
                    >
                      Create a duplicate voucher
                    </Button>
                  )}
                  {addNoteMode.action === "reject" && (
                    <Button
                      variant="contained"
                      fullWidth
                      color="primary"
                      style={{ marginTop: "24px" }}
                      disabled={addNoteMode.required && acionNote.length === 0}
                      onClick={(e) =>
                        setVoucherStatus(voucher.id, {
                          status: "rejected",
                          note: acionNote,
                          sendEmail: true,
                        })
                      }
                    >
                      Reject
                    </Button>
                  )}
                  {addNoteMode.action === "approve" && (
                    <Button
                      variant="contained"
                      fullWidth
                      color="primary"
                      style={{ marginTop: "24px" }}
                      disabled={addNoteMode.required && acionNote.length === 0}
                      onClick={(e) =>
                        setVoucherStatus(voucher.id, {
                          status: "approved",
                          note: acionNote,
                          sendEmail: true,
                        })
                      }
                    >
                      Yes
                    </Button>
                  )}
                  {addNoteMode.action === "resend" && (
                    <Button
                      variant="contained"
                      fullWidth
                      color="primary"
                      style={{ marginTop: "24px" }}
                      disabled={addNoteMode.required && acionNote.length === 0}
                      onClick={(e) =>
                        setVoucherStatus(voucher.id, {
                          status: "approved",
                          note: acionNote,
                          sendEmail: true,
                        })
                      }
                    >
                      Send approval email
                    </Button>
                  )}
                  {addNoteMode.action === "cancel" && (
                    <Button
                      variant="contained"
                      fullWidth
                      color="primary"
                      style={{ marginTop: "24px" }}
                      disabled={addNoteMode.required && acionNote.length === 0}
                      onClick={(e) =>
                        setVoucherStatus(voucher.id, {
                          status: "cancelled",
                          note: acionNote,
                          sendEmail: false,
                        })
                      }
                    >
                      Confirm cancel voucher
                    </Button>
                  )}
                  {addNoteMode.action === "addNoteOnly" && (
                    <Button
                      variant="contained"
                      fullWidth
                      color="primary"
                      style={{ marginTop: "24px" }}
                      disabled={addNoteMode.required && acionNote.length === 0}
                      onClick={(e) =>
                        setVoucherStatus(voucher.id, {
                          status: current.status,
                          note: acionNote,
                          sendEmail: false,
                        })
                      }
                    >
                      Add Note
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    fullWidth
                    color="primary"
                    style={{ marginTop: "24px" }}
                    onClick={(e) => setAddNoteMode({ showNote: false })}
                  >
                    Cancel
                  </Button>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  {(current.status === "paid" || current.status === "cancelled") && (
                    <Button
                      variant="contained"
                      fullWidth
                      color="primary"
                      style={{ marginTop: "24px" }}
                      onClick={(e) =>
                        setAddNoteMode({
                          showNote: true,
                          required: true,
                          noteLabel: "What is the reason to re-issue voucher?",
                          action: "reissue",
                        })
                      }
                    >
                      Re-issue Voucher
                    </Button>
                  )}
                  {current.status === "rejected" && (
                    <Button
                      variant="contained"
                      fullWidth
                      color="primary"
                      style={{ marginTop: "24px" }}
                      onClick={(e) =>
                        setAddNoteMode({
                          showNote: true,
                          required: true,
                          noteLabel: "Why change of mind?",
                          action: "approve",
                        })
                      }
                    >
                      Change to Approve
                    </Button>
                  )}
                  {current.status === "approved" && (
                    <>
                      <Button
                        variant="contained"
                        fullWidth
                        color="primary"
                        style={{ marginTop: "24px" }}
                        onClick={(e) =>
                          setAddNoteMode({
                            showNote: true,
                            required: false,
                            noteLabel: "Add an optional note",
                            action: "resend",
                          })
                        }
                      >
                        Re-send approval Email
                      </Button>
                      <Button
                        variant="contained"
                        fullWidth
                        color="primary"
                        style={{ marginTop: "24px" }}
                        onClick={(e) =>
                          setAddNoteMode({
                            showNote: true,
                            required: true,
                            noteLabel: "Record a reason for the cancellation",
                            action: "cancel",
                          })
                        }
                      >
                        Cancel Voucher
                      </Button>
                    </>
                  )}
                  {current.status === "pending" && (
                    <>
                      <Button
                        variant="contained"
                        fullWidth
                        color="primary"
                        style={{ marginTop: "24px" }}
                        onClick={(e) =>
                          setAddNoteMode({
                            showNote: true,
                            confirmNote:
                              "Are you sure you want to approve this voucher? You cannot undo this later.",
                            action: "approve",
                          })
                        }
                      >
                        Approve
                      </Button>
                      <Button
                        variant="contained"
                        fullWidth
                        color="primary"
                        style={{ marginTop: "24px" }}
                        onClick={(e) =>
                          setAddNoteMode({
                            showNote: true,
                            required: true,
                            noteLabel: "Reason for rejection",
                            action: "reject",
                          })
                        }
                      >
                        Reject
                      </Button>
                    </>
                  )}
                  {current.status !== "deleted" && current.status !== "cancelled" && (
                    <Button
                      variant="contained"
                      fullWidth
                      color="primary"
                      style={{ marginTop: "24px" }}
                      onClick={(e) =>
                        setAddNoteMode({
                          showNote: true,
                          required: true,
                          noteLabel: "Type the note",
                          action: "addNoteOnly",
                        })
                      }
                    >
                      Add Note
                    </Button>
                  )}
                </React.Fragment>
              )}
            </React.Fragment>
          )}
          {userType === UserTypes.neweyes && voucher.actions.length > 0 && (
            <React.Fragment>
              <Divider style={{ marginTop: "12px", marginBottom: "12px" }} />
              <Typography variant="button" color="textSecondary">
                VOUCHER HISTORY
              </Typography>
              <VoucherActionList actions={voucher.actions} />
            </React.Fragment>
          )}
        </div>
      )}
    </React.Fragment>
  );
};

export default VoucherPanel;
